/**
 * Apply AI treatment plan revision operations to useQuestionnaireForm state.
 */

import type { Questionnaire, QuestionnaireItem, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import type { QuestionnaireFormLoadedState } from '@medplum/react-hooks';
import { findQuestionnaireItemByLinkIdSuffix, getAnswerString } from './treatmentPlanFormHelpers';

export type TreatmentPlanRevisionAction =
  | 'replace-answer'
  | 'append-answer'
  | 'add-repeatable-group';

export interface TreatmentPlanRevisionInitialAnswer {
  linkId: string;
  valueString: string;
}

export interface TreatmentPlanRevisionOperation {
  action: TreatmentPlanRevisionAction;
  linkId: string;
  parentLinkId?: string;
  /** 0-based index of the parent repeating group instance (default 0). */
  parentInstanceIndex?: number;
  /** 0-based index among matching fields; -1 targets the last match (e.g. after add-repeatable-group). */
  instanceIndex?: number;
  valueString?: string;
  /** Populated when action is add-repeatable-group — values for fields inside the new instance. */
  initialAnswers?: TreatmentPlanRevisionInitialAnswer[];
  rationale?: string;
}

export interface TreatmentPlanRevisionSuggestion {
  summary: string;
  warnings?: string[];
  operations: TreatmentPlanRevisionOperation[];
  /**
   * When true (guided/quick re-generate onto an already-filled draft), clear
   * symptoms/goals/objectives under existing diagnoses before applying so
   * add-repeatable-group does not duplicate populated instances.
   */
  replaceExistingContent?: boolean;
}

export function collectTreatmentPlanLinkIds(questionnaire: Questionnaire): Set<string> {
  const ids = new Set<string>();
  function walk(items: QuestionnaireItem[] | undefined): void {
    for (const item of items ?? []) {
      if (item.linkId) ids.add(item.linkId);
      walk(item.item);
    }
  }
  walk(questionnaire.item);
  return ids;
}

export function summarizeRepeatableQuestionnaireStructure(
  items: QuestionnaireItem[] | undefined,
  indent = 0,
): string {
  const lines: string[] = [];
  for (const item of items ?? []) {
    const repeat = item.repeats ? ' [repeats]' : '';
    const label = item.text ? `: ${item.text}` : '';
    lines.push(`${'  '.repeat(indent)}- ${item.linkId} (${item.type}${repeat})${label}`);
    if (item.item?.length) {
      lines.push(summarizeRepeatableQuestionnaireStructure(item.item, indent + 1));
    }
  }
  return lines.join('\n');
}

function childLinkIdsOf(item: QuestionnaireItem | undefined): Set<string> {
  const ids = new Set<string>();
  function walk(items: QuestionnaireItem[] | undefined): void {
    for (const child of items ?? []) {
      if (child.linkId) ids.add(child.linkId);
      walk(child.item);
    }
  }
  walk(item?.item);
  return ids;
}

export function validateTreatmentPlanRevisionOperations(
  questionnaire: Questionnaire,
  operations: TreatmentPlanRevisionOperation[],
): { valid: boolean; errors: string[] } {
  const allowed = collectTreatmentPlanLinkIds(questionnaire);
  const errors: string[] = [];
  for (const op of operations) {
    if (!allowed.has(op.linkId)) {
      errors.push(`Unknown linkId "${op.linkId}"`);
    }
    if (op.parentLinkId && !allowed.has(op.parentLinkId)) {
      errors.push(`Unknown parentLinkId "${op.parentLinkId}"`);
    }
    if (op.action !== 'add-repeatable-group' && !op.valueString?.trim()) {
      errors.push(`Operation for "${op.linkId}" requires valueString`);
    }
    if (op.action === 'add-repeatable-group' && !op.parentLinkId) {
      errors.push(`add-repeatable-group for "${op.linkId}" requires parentLinkId`);
    }
    if (op.initialAnswers?.length) {
      const groupItem = findQuestionnaireItemByLinkId(questionnaire.item, op.linkId);
      const allowedChildren = childLinkIdsOf(groupItem);
      for (const answer of op.initialAnswers) {
        if (!allowedChildren.has(answer.linkId)) {
          errors.push(`initialAnswers linkId "${answer.linkId}" is not a child of "${op.linkId}"`);
        }
        if (!answer.valueString?.trim()) {
          errors.push(`initialAnswers for "${answer.linkId}" requires valueString`);
        }
      }
    }
  }
  return { valid: errors.length === 0, errors };
}

function findQuestionnaireItemByLinkId(
  items: QuestionnaireItem[] | undefined,
  linkId: string,
): QuestionnaireItem | undefined {
  for (const item of items ?? []) {
    if (item.linkId === linkId) return item;
    const nested = findQuestionnaireItemByLinkId(item.item, linkId);
    if (nested) return nested;
  }
  return undefined;
}

interface ResponseMatch {
  context: QuestionnaireResponseItem[];
  responseItem: QuestionnaireResponseItem;
}

function getResponseItemsRoot(formState: QuestionnaireFormLoadedState): QuestionnaireResponseItem[] {
  return formState.responseItems ?? formState.questionnaireResponse.item ?? [];
}

function findAllResponseMatches(
  items: QuestionnaireResponseItem[],
  linkId: string,
  ancestorContext: QuestionnaireResponseItem[] = [],
): ResponseMatch[] {
  const matches: ResponseMatch[] = [];
  for (const item of items) {
    const context = ancestorContext;
    if (item.linkId === linkId) {
      matches.push({ context, responseItem: item });
    }
    matches.push(...findAllResponseMatches(item.item ?? [], linkId, [...context, item]));
  }
  return matches;
}

function indexAmongSiblingRepeats(
  siblings: QuestionnaireResponseItem[] | undefined,
  instance: QuestionnaireResponseItem,
): number {
  if (!siblings) return 0;
  const sameLinkId = siblings.filter((sibling) => sibling.linkId === instance.linkId);
  const idx = sameLinkId.indexOf(instance);
  return idx >= 0 ? idx : 0;
}

function filterMatchesByParentScope(
  matches: ResponseMatch[],
  root: QuestionnaireResponseItem[],
  parentLinkId: string,
  parentInstanceIndex: number,
): ResponseMatch[] {
  return matches.filter((match) => {
    const parentIdx = match.context.findIndex((item) => item.linkId === parentLinkId);
    if (parentIdx < 0) return false;
    const parentInstance = match.context[parentIdx];
    const siblings = parentIdx === 0 ? root : match.context[parentIdx - 1]?.item;
    return indexAmongSiblingRepeats(siblings, parentInstance) === parentInstanceIndex;
  });
}

function resolveResponseMatch(
  formState: QuestionnaireFormLoadedState,
  op: TreatmentPlanRevisionOperation,
): ResponseMatch | undefined {
  const root = getResponseItemsRoot(formState);
  let matches = findAllResponseMatches(root, op.linkId);
  if (op.parentLinkId) {
    matches = filterMatchesByParentScope(
      matches,
      root,
      op.parentLinkId,
      op.parentInstanceIndex ?? 0,
    );
  }
  if (matches.length === 0) return undefined;
  const idx = op.instanceIndex ?? 0;
  if (idx === -1) return matches[matches.length - 1];
  return matches[idx];
}

function findQuestionnaireItemPath(
  items: QuestionnaireItem[] | undefined,
  linkId: string,
  path: QuestionnaireItem[] = [],
): QuestionnaireItem[] | undefined {
  for (const item of items ?? []) {
    const next = [...path, item];
    if (item.linkId === linkId) return next;
    const nested = findQuestionnaireItemPath(item.item, linkId, next);
    if (nested) return nested;
  }
  return undefined;
}

function getGroupInstances(
  root: QuestionnaireResponseItem[],
  context: QuestionnaireResponseItem[],
  groupLinkId: string,
): QuestionnaireResponseItem[] {
  if (context.length === 0) {
    return root.filter((item) => item.linkId === groupLinkId);
  }
  const parent = context[context.length - 1];
  return (parent.item ?? []).filter((item) => item.linkId === groupLinkId);
}

function resolveGroupInstance(
  formState: QuestionnaireFormLoadedState,
  context: QuestionnaireResponseItem[],
  groupItem: QuestionnaireItem,
  instanceIndex: number,
): QuestionnaireResponseItem {
  const groupLinkId = groupItem.linkId!;
  let instances = getGroupInstances(getResponseItemsRoot(formState), context, groupLinkId);
  while (instances.length <= instanceIndex) {
    formState.onAddGroup(context, groupItem);
    instances = getGroupInstances(getResponseItemsRoot(formState), context, groupLinkId);
  }
  return instances[instanceIndex]!;
}

/** Ensure plan-info + at least one diagnosis block exist (empty draft treatment plans). */
export function ensureTreatmentPlanBaseScaffold(
  formState: QuestionnaireFormLoadedState,
  questionnaire: Questionnaire,
): void {
  const planInfo = findQuestionnaireItemByLinkIdSuffix(questionnaire.item, '-plan-info');
  const diagnosis = findQuestionnaireItemByLinkIdSuffix(questionnaire.item, '-diagnosis');
  const root = getResponseItemsRoot(formState);

  if (planInfo && getGroupInstances(root, [], planInfo.linkId!).length === 0) {
    formState.onAddGroup([], planInfo);
  }
  const rootAfterPlan = getResponseItemsRoot(formState);
  if (diagnosis && getGroupInstances(rootAfterPlan, [], diagnosis.linkId!).length === 0) {
    formState.onAddGroup([], diagnosis);
  }
}

function instanceIndexForAncestor(
  ancestor: QuestionnaireItem,
  op: Pick<TreatmentPlanRevisionOperation, 'parentLinkId' | 'parentInstanceIndex'>,
): number {
  if (ancestor.linkId === op.parentLinkId && op.parentInstanceIndex !== undefined) {
    return op.parentInstanceIndex;
  }
  return 0;
}

/** Create repeating group instances along the path to a target linkId. */
export function ensureResponseContextForLinkId(
  formState: QuestionnaireFormLoadedState,
  questionnaire: Questionnaire,
  linkId: string,
  op: Pick<TreatmentPlanRevisionOperation, 'parentLinkId' | 'parentInstanceIndex'>,
): QuestionnaireResponseItem[] {
  ensureTreatmentPlanBaseScaffold(formState, questionnaire);

  const itemPath = findQuestionnaireItemPath(questionnaire.item, linkId);
  if (!itemPath || itemPath.length < 2) return [];

  const ancestors = itemPath.slice(0, -1).filter((item) => item.type === 'group');
  let context: QuestionnaireResponseItem[] = [];

  for (const ancestor of ancestors) {
    const idx = ancestor.repeats ? instanceIndexForAncestor(ancestor, op) : 0;
    if (ancestor.repeats || getGroupInstances(getResponseItemsRoot(formState), context, ancestor.linkId!).length === 0) {
      const instance = resolveGroupInstance(formState, context, ancestor, idx);
      context = [...context, instance];
    } else {
      const instances = getGroupInstances(getResponseItemsRoot(formState), context, ancestor.linkId!);
      if (instances[0]) context = [...context, instances[0]];
    }
  }

  return context;
}

function findLastGroupInstance(
  parentItem: QuestionnaireResponseItem,
  groupLinkId: string,
): QuestionnaireResponseItem | undefined {
  const instances = (parentItem.item ?? []).filter((child) => child.linkId === groupLinkId);
  return instances[instances.length - 1];
}

/** True when no field anywhere in the subtree has a non-blank answer. */
function isResponseSubtreeEmpty(item: QuestionnaireResponseItem): boolean {
  const hasAnswer = (item.answer ?? []).some((answer) => {
    if (typeof answer.valueString === 'string') return answer.valueString.trim().length > 0;
    return Object.values(answer).some((value) => value !== undefined);
  });
  if (hasAnswer) return false;
  return (item.item ?? []).every(isResponseSubtreeEmpty);
}

function clearAnswersDeep(item: QuestionnaireResponseItem): void {
  if (item.answer?.length) item.answer = [];
  for (const child of item.item ?? []) {
    clearAnswersDeep(child);
  }
}

/**
 * Keep diagnosis codes / plan-info, but reset generated clinical content so a
 * second guided/quick generate can fill instance 0 instead of appending duplicates.
 */
export function clearTreatmentPlanContentKeepingDiagnoses(
  formState: QuestionnaireFormLoadedState,
  questionnaire: Questionnaire,
): void {
  const diagnosisDef = findQuestionnaireItemByLinkIdSuffix(questionnaire.item, '-diagnosis');
  if (!diagnosisDef?.linkId) return;

  const root = getResponseItemsRoot(formState);
  const diagnosisInstances = root.filter((item) => item.linkId === diagnosisDef.linkId);

  for (const diagnosisInstance of diagnosisInstances) {
    const children = diagnosisInstance.item ?? [];
    const nextChildren: QuestionnaireResponseItem[] = [];

    for (const child of children) {
      const linkId = child.linkId ?? '';
      // Preserve the diagnosis code answer; clear free-text presenting problem.
      if (linkId.endsWith('-diagnosis-code')) {
        nextChildren.push(child);
        continue;
      }
      if (linkId.endsWith('-presenting-problem')) {
        clearAnswersDeep(child);
        nextChildren.push(child);
        continue;
      }

      // Keep a single empty scaffold instance for repeating clinical groups.
      if (
        linkId.endsWith('-behavioral-definition')
        || linkId.endsWith('-goal')
        || linkId.endsWith('-objective')
      ) {
        if (nextChildren.some((kept) => kept.linkId === linkId)) continue;
        clearAnswersDeep(child);
        // Nested objectives under goals: keep first objective only.
        if (child.item?.length) {
          const nested: QuestionnaireResponseItem[] = [];
          for (const nestedChild of child.item) {
            const nestedId = nestedChild.linkId ?? '';
            if (nestedId.endsWith('-objective')) {
              if (nested.some((kept) => kept.linkId === nestedId)) continue;
              clearAnswersDeep(nestedChild);
              if (nestedChild.item?.length) {
                const objectiveNested: QuestionnaireResponseItem[] = [];
                for (const leaf of nestedChild.item) {
                  clearAnswersDeep(leaf);
                  objectiveNested.push(leaf);
                }
                nestedChild.item = objectiveNested;
              }
              nested.push(nestedChild);
              continue;
            }
            clearAnswersDeep(nestedChild);
            nested.push(nestedChild);
          }
          child.item = nested;
        }
        nextChildren.push(child);
        continue;
      }

      clearAnswersDeep(child);
      nextChildren.push(child);
    }

    diagnosisInstance.item = nextChildren;
  }

  // Nudge the form state so dependent UIs refresh from the mutated response tree.
  const planInfo = findQuestionnaireItemByLinkIdSuffix(questionnaire.item, '-plan-info');
  if (planInfo) {
    const planInstances = getResponseItemsRoot(formState).filter((item) => item.linkId === planInfo.linkId);
    const first = planInstances[0];
    if (first) {
      const frequency = findQuestionnaireItemByLinkIdSuffix(planInfo.item, '-treatment-frequency');
      if (frequency) {
        const current = first.item?.find((item) => item.linkId === frequency.linkId);
        formState.onChangeAnswer([first], frequency, current?.answer ?? []);
      }
    }
  }
}

export function formatTreatmentPlanRevisionOperation(
  op: TreatmentPlanRevisionOperation,
  questionnaire: Questionnaire,
): string {
  const qItem = findQuestionnaireItemByLinkId(questionnaire.item, op.linkId);
  const label = qItem?.text ?? op.linkId;
  const actionLabel =
    op.action === 'replace-answer'
      ? 'Replace'
      : op.action === 'append-answer'
        ? 'Append to'
        : 'Add';
  const scopeParts: string[] = [];
  if (op.parentLinkId) {
    scopeParts.push(`parent ${op.parentLinkId} #${(op.parentInstanceIndex ?? 0) + 1}`);
  }
  if (op.instanceIndex !== undefined && op.instanceIndex >= 0) {
    scopeParts.push(`instance #${op.instanceIndex + 1}`);
  } else if (op.instanceIndex === -1) {
    scopeParts.push('last instance');
  }
  const scope = scopeParts.length > 0 ? ` (${scopeParts.join(', ')})` : '';
  if (op.action === 'add-repeatable-group') {
    const fields = op.initialAnswers?.map((a) => a.linkId).join(', ') ?? 'empty group';
    return `${actionLabel} ${label}${scope}: ${fields}`;
  }
  return `${actionLabel} ${label}${scope}`;
}

export function applyTreatmentPlanRevisionOperations(
  formState: QuestionnaireFormLoadedState,
  questionnaire: Questionnaire,
  operations: TreatmentPlanRevisionOperation[],
  opts?: { replaceExistingContent?: boolean },
): void {
  const validation = validateTreatmentPlanRevisionOperations(questionnaire, operations);
  if (!validation.valid) {
    throw new Error(validation.errors.join('; '));
  }

  ensureTreatmentPlanBaseScaffold(formState, questionnaire);
  if (opts?.replaceExistingContent) {
    clearTreatmentPlanContentKeepingDiagnoses(formState, questionnaire);
  }

  for (const op of operations) {
    if (op.action === 'add-repeatable-group') {
      const parentLinkId = op.parentLinkId!;
      const parentItem = findQuestionnaireItemByLinkId(questionnaire.item, parentLinkId);
      const childItem = findQuestionnaireItemByLinkId(questionnaire.item, op.linkId);
      if (!parentItem?.repeats || !childItem) {
        throw new Error(`Cannot add repeatable group for ${op.linkId}`);
      }

      // Top-level repeating groups (e.g. diagnosis) have no parent group; the
      // operation model self-references parentLinkId and the instance is added
      // at the response root.
      const isTopLevelSelfParent =
        parentLinkId === op.linkId
        && (findQuestionnaireItemPath(questionnaire.item, op.linkId) ?? [])
          .slice(0, -1)
          .every((ancestor) => ancestor.type !== 'group');

      // Empty forms pre-create one instance of each repeating group. When the
      // last existing instance is completely blank, fill it instead of
      // appending a duplicate next to an untouched placeholder. Only the last
      // instance qualifies so follow-up operations with instanceIndex -1
      // ("last match") still target the instance this operation produced.
      const reuseIfLastIsEmpty = (
        instances: QuestionnaireResponseItem[],
      ): QuestionnaireResponseItem | undefined => {
        const last = instances[instances.length - 1];
        return last && isResponseSubtreeEmpty(last) ? last : undefined;
      };

      let context: QuestionnaireResponseItem[];
      let newInstance: QuestionnaireResponseItem | undefined;
      if (isTopLevelSelfParent) {
        context = [];
        newInstance = reuseIfLastIsEmpty(
          getResponseItemsRoot(formState).filter((item) => item.linkId === childItem.linkId),
        );
        if (!newInstance) {
          formState.onAddGroup(context, childItem);
          const updated = getResponseItemsRoot(formState)
            .filter((item) => item.linkId === childItem.linkId);
          newInstance = updated[updated.length - 1];
        }
      } else {
        context = ensureResponseContextForLinkId(formState, questionnaire, op.linkId, op);
        const parentInstance = context[context.length - 1];
        if (!parentInstance || parentInstance.linkId !== parentLinkId) {
          throw new Error(`Could not resolve parent group "${parentLinkId}" for ${op.linkId}`);
        }
        newInstance = reuseIfLastIsEmpty(
          (parentInstance.item ?? []).filter((child) => child.linkId === childItem.linkId),
        );
        if (!newInstance) {
          formState.onAddGroup(context, childItem);
          newInstance = findLastGroupInstance(parentInstance, childItem.linkId!);
        }
      }

      if (!op.initialAnswers?.length) continue;

      if (!newInstance) {
        throw new Error(`Could not locate new group instance for ${op.linkId}`);
      }

      // initialAnswers may target fields nested inside the new instance (e.g. a
      // new goal's objective-text lives in the goal's nested objective group).
      // Descend through intermediate group instances — using the last existing
      // instance, creating one only when absent — so nested answers land inside
      // the proper subgroup instead of as misplaced direct children.
      for (const answer of op.initialAnswers) {
        const fieldPath = findQuestionnaireItemPath(childItem.item, answer.linkId);
        if (!fieldPath?.length) continue;
        const fieldItem = fieldPath[fieldPath.length - 1];

        let answerContext = [...context, newInstance];
        for (const intermediate of fieldPath.slice(0, -1).filter((item) => item.type === 'group')) {
          const container = answerContext[answerContext.length - 1];
          let instances = (container.item ?? []).filter((child) => child.linkId === intermediate.linkId);
          if (instances.length === 0) {
            formState.onAddGroup(answerContext, intermediate);
            instances = (container.item ?? []).filter((child) => child.linkId === intermediate.linkId);
          }
          const instance = instances[instances.length - 1];
          if (!instance) break;
          answerContext = [...answerContext, instance];
        }

        formState.onChangeAnswer(answerContext, fieldItem, [{ valueString: answer.valueString.trim() }]);
      }
      continue;
    }

    const qItem = findQuestionnaireItemByLinkId(questionnaire.item, op.linkId);
    if (!qItem) continue;

    const context = ensureResponseContextForLinkId(formState, questionnaire, op.linkId, op);
    const located = resolveResponseMatch(formState, op);
    const current = located ? getAnswerString(located.responseItem) : '';
    const nextValue =
      op.action === 'append-answer'
        ? [current, op.valueString?.trim() ?? ''].filter(Boolean).join('\n\n')
        : (op.valueString ?? '');

    formState.onChangeAnswer(context, qItem, [{ valueString: nextValue }]);
  }
}
